import { useEffect, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { DOCK } from './nav';
import { byGates, useNavGates } from './useNavGates';
import { t } from '../i18n/i18n';

const icon = (name) => ({ '--icon': `url(/icons/${name}.svg)` });

/** The rule NavLink applies without `end`, so the fill can't disagree with it. */
const isCurrent = (to, pathname) => pathname === to || pathname.startsWith(`${to}/`);

const reducedMotion = () =>
  Boolean(window.matchMedia?.('(prefers-reduced-motion: reduce)').matches);

export default function Dock() {
  const { pathname } = useLocation();
  const rail = useRef(null);
  const items = DOCK.filter(byGates(useNavGates()));
  const index = items.findIndex((item) => isCurrent(item.to, pathname));

  /**
   * Where the active fill rests while it has no item of its own — the routes
   * outside the dock (Profile, Help centre). It fades out where it stood
   * instead of sliding back to the first icon.
   */
  const [resting, setResting] = useState(index < 0 ? 0 : index);

  useEffect(() => {
    if (index >= 0) setResting(index);
  }, [index]);

  /**
   * The fill is placed from the active item's index, not by measuring it.
   *
   * That matters for the animation: an index is known during the first render,
   * so the fill is already in position at the first paint and has no phantom
   * "before" state to travel from. Measuring in an effect always leaves one,
   * whatever the effect's timing — and a guard flag can't fix it either, since
   * StrictMode runs the effect twice and the flag is already spent by the
   * second pass. CSS turns the index into a distance, so the item size and gap
   * stay in one place.
   */
  const at = index < 0 ? resting : index;

  // The row is wider than the screen, so the current item is often out of view
  // on arrival. scrollLeft directly rather than scrollIntoView, which would
  // also scroll the page behind it.
  useEffect(() => {
    const track = rail.current;
    const current = track?.querySelector('.dock__item.is-active');
    if (!track || !current) return;

    track.scrollTo({
      left: current.offsetLeft - (track.clientWidth - current.offsetWidth) / 2,
      behavior: reducedMotion() ? 'auto' : 'smooth',
    });
  }, [pathname]);

  return (
    <nav className="dock" aria-label={t('Main')}>
      <div className="dock__rail" ref={rail}>
        {/* Inside the track, so it scrolls along with the icons it marks. */}
        <span
          className={`dock__fill${index >= 0 ? ' is-on' : ''}`}
          style={{ '--fill-i': at }}
          aria-hidden="true"
        />

        {items.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) => `dock__item${isActive ? ' is-active' : ''}`}
            aria-label={t(item.label)}
            title={t(item.label)}
          >
            <span className="icon dock__icon" style={icon(item.icon)} />
          </NavLink>
        ))}
      </div>
    </nav>
  );
}
